package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.modifiers.translateX
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// Transform only. Never opacity — see the note on [LRevealStyle].
val LRevealUp = Keyframes {
    from { Modifier.translateY(18.px) }
    to { Modifier.translateY(0.px) }
}

val LRevealLeft = Keyframes {
    from { Modifier.translateX(22.px) }
    to { Modifier.translateX(0.px) }
}

/** Scale rather than fade, for the same reason: a frozen frame must still be readable. */
/**
 * The settle for something already in its final position.
 *
 * **Named for what it does, after being named for what it does not.** It was `LRevealFade`, and it
 * has never faded: it scales 0.985 to 1. The name mattered because this file's entire argument,
 * set out below, is that nothing here may animate opacity — a suspended tab strands content at
 * `opacity: 0` and the reader gets a blank page. A keyframe called `Fade` in a file that forbids
 * fading is an invitation to write one.
 */
val LRevealSettle = Keyframes {
    from { Modifier.scale(0.985) }
    to { Modifier.scale(1.0) }
}

/**
 * How a revealed block arrives.
 *
 * Distance is small on purpose. A 14px rise reads as the page settling; a 60px rise reads as a
 * slide deck, and by the third section the reader is waiting for the content rather than reading it.
 */
// KSP scans for `val keyframes` to catch keyframes declared inside a function, where they cannot be
// registered. This is a constructor parameter holding a reference to one of the three declared at the
// top of this file, which are registered normally — so the warning is a false positive, and one that
// reads exactly like a real "your animation silently does nothing" defect. Suppressed rather than
// worked around by renaming, because `keyframes` is what the property holds.
@Suppress("LOCAL_KEYFRAMES")
enum class LRevealFrom(internal val keyframes: Keyframes) {
    /** The default. Content rises slightly into place. */
    BELOW(LRevealUp),

    /** For something entering beside a column of text, such as a figure. */
    RIGHT(LRevealLeft),

    /**
     * No travel — a slight settle in place, for something already where it belongs, like an image.
     *
     * This said "only opacity", which is the one thing this file exists to forbid and the opposite
     * of what the keyframe does. A reader trusting it would have written the defect the doctrine
     * below was written to prevent.
     */
    IN_PLACE(LRevealSettle),
}

/**
 * Why nothing here animates opacity.
 *
 * Three versions of this component hid the landing page, each for a different reason, and the third
 * is the instructive one:
 *
 * 1. An `IntersectionObserver` driven from a Compose effect. Compose HTML runs effects off
 *    `requestAnimationFrame`, which a browser suspends for a tab it is not painting, so the
 *    observer was never attached and nothing ever set the elements visible.
 * 2. A `delay()` safety timer meant to catch that — on the same stalled clock.
 * 3. A pure CSS fade. No Kotlin involved at all, and it *still* stranded content: a suspended tab
 *    does not advance the animation clock either, so the element sat frozen on frame zero at
 *    `opacity: 0`. `fill-mode: forwards` did not help, because frame zero is where it already was.
 *
 * The rule that survives all three: **an entrance must never be able to hide content.** Animating
 * transform alone satisfies it unconditionally — a frozen frame is text shifted 18px, which is
 * readable, rather than text that is not there. It is a quieter effect than a fade, and it is the
 * one that cannot fail.
 */
val LRevealStyle = CssStyle.base {
    Modifier.opacity(1)
}

/**
 * Reveals its content with a short entrance.
 *
 * **There is deliberately no stagger delay.** A delay needs `animation-fill-mode: both` to hold the
 * opening frame until the item's turn, and `both` is what made the second version of this component
 * ship invisible content: a tab the browser is not painting never starts the animation, so the
 * element sat on its opening frame — `opacity: 0` — indefinitely. With `forwards` there is no
 * held opening frame, so an animation that never runs leaves the content exactly where it already
 * was: visible. Losing a 70ms cascade is a small price for that guarantee.
 *
 * `prefers-reduced-motion` is handled globally in `initLingvoTheme` rather than here, so a new
 * component cannot forget it.
 */
@Composable
fun LReveal(
    modifier: Modifier = Modifier,
    from: LRevealFrom = LRevealFrom.BELOW,
    content: @Composable () -> Unit,
) {
    Div(
        LRevealStyle.toModifier()
            .then(modifier)
            .animation(
                from.keyframes.toAnimation(
                    colorMode = ColorMode.current,
                    duration = Motion.SlowMs.ms,
                    timingFunction = Motion.EaseOutTiming,
                    // Forwards, never Both. See the note above: `both` can strand content on its
                    // opening frame in a tab the browser has stopped painting.
                    fillMode = AnimationFillMode.Forwards,
                ),
            )
            .toAttrs(),
    ) {
        content()
    }
}
